import PaymentDetails from './DriverPaymentDetails';
import {driverCapabilities,DRIVER_TRIP_UNAVAILABLE} from '../../api/driverPartner';
import {formatDriverCurrency} from '../../utils/driverMoney';
import React from 'react';
import {cancelOrder} from '../../store/slices/driverSlice';
import { useDispatch, useSelector } from 'react-redux';
import { Card, Button, Badge, Row, Col, Alert } from 'react-bootstrap';
import { FaMapMarkerAlt, FaBox } from 'react-icons/fa';

const getStatusBadge = (status) => {
  const map = {
    PENDING: 'warning',
    ASSIGNED: 'info',
    PICKED_UP: 'primary',
    IN_TRANSIT: 'primary',
    DELIVERED: 'success',
    CANCELLED: 'danger',
  };
  return <Badge bg={map[status] || 'secondary'}>{status}</Badge>;
};

const DriverOrderList = ({ orders = [], onSelectOrder, readOnly = false }) => {
  const dispatch=useDispatch();
  const canAccept = useSelector(state => state.wallet?.data?.canAcceptOrders === true && !state.wallet?.loading && !state.wallet?.error);
  const { loading, error } = useSelector((state) => state.driver);

  if (loading) {
    return (
      <div>
        <div className="driver-list-header mb-4">
          <div>
            <span className="driver-list-kicker">{readOnly ? 'Archive' : 'Live Queue'}</span>
            <h5 className="mb-1">{readOnly ? 'Delivery History' : 'Available Orders'}</h5>
          </div>
        </div>
        <Row className="g-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <Col md={6} key={`driver-skeleton-${index}`}>
              <Card className="order-card h-100 shadow-sm skeleton-card">
                <Card.Body>
                  <div className="d-flex justify-content-between align-items-start mb-3">
                    <div className="skeleton-line skeleton-line-short" />
                    <div className="skeleton-pill" />
                  </div>
                  <div className="skeleton-line mb-2" />
                  <div className="skeleton-line mb-3" />
                  <div className="skeleton-line mb-2" />
                  <div className="skeleton-line skeleton-line-medium mb-3" />
                  <div className="d-flex justify-content-between align-items-center">
                    <div className="skeleton-line skeleton-line-short" />
                    <div className="skeleton-button" />
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  if (orders.length === 0) {
    return (
      <div className="empty-state-card driver-empty-state">
        <div className="empty-state-icon">{readOnly ? '🧾' : '🚚'}</div>
        <h4>{readOnly ? 'No completed deliveries yet' : 'No orders available right now'}</h4>
        <p>
          {readOnly
            ? 'Finished deliveries will appear here for quick review once you complete your first order.'
            : 'As soon as a delivery is assignable, it will show up here in the live queue.'}
        </p>
        <div className="empty-state-pills">
          <span>{readOnly ? 'Completion log' : 'Live queue'}</span>
          <span>{readOnly ? 'Order archive' : 'Pickup alerts'}</span>
          <span>{readOnly ? 'Fast review' : 'Route ready'}</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="driver-list-header mb-4">
        <div>
          <span className="driver-list-kicker">{readOnly ? 'Archive' : 'Live Queue'}</span>
          <h5 className="mb-1">{readOnly ? 'Delivery History' : 'Available Orders'}</h5>
          <p className="text-muted mb-0">
            {readOnly ? 'Completed deliveries remain visible here for quick review.' : 'Review parcel details and choose the next order to start.'}
          </p>
        </div>
      </div>
      <Row className="g-3">
        {orders.map((order) => (
          <Col md={6} key={order.id || order.orderId}>
            <Card className="order-card h-100 shadow-sm">
              <Card.Body>
                <div className="d-flex justify-content-between align-items-start mb-3">
                  <div>
                    <h6 className="mb-1 fw-bold">
                      #{String(order.id || order.orderId || '').slice(0, 8)}
                    </h6>
                    <small className="text-muted">{order.vehicleType}</small>
                  </div>
                  {getStatusBadge(order.status)}
                </div>

                <div className="mb-2">
                  <div className="d-flex align-items-start mb-2">
                    <FaMapMarkerAlt className="text-success me-2 mt-1" />
                    <div>
                      <small className="text-muted d-block">Pickup</small>
                      <span className="fw-medium">
                        {order.pickupLocation?.name || order.pickupLocation?.address || 'N/A'}
                      </span>
                    </div>
                  </div>
                  <div className="d-flex align-items-start mb-2">
                    <FaMapMarkerAlt className="text-danger me-2 mt-1" />
                    <div>
                      <small className="text-muted d-block">Drop</small>
                      <span className="fw-medium">
                        {order.dropLocation?.name || order.dropLocation?.address || 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="d-flex align-items-center mb-3">
                  <FaBox className="text-warning me-2" />
                  <small>
                    {order.parcelSize} &middot; {order.parcelWeight}kg
                  </small>
                </div>

                <PaymentDetails order={order}/>{!readOnly&&!driverCapabilities.accept&&<Alert variant="info">{DRIVER_TRIP_UNAVAILABLE}</Alert>}
                <div className="d-flex justify-content-between align-items-center order-card-footer">
                  <span className="fw-bold text-primary order-amount">
                    Customer fare estimate: {formatDriverCurrency(order.estimatedCost,'rupees')}
                  </span>
                  {!readOnly && order.status==='PENDING' && <Button variant="outline-danger" size="sm" disabled={loading||!driverCapabilities.cancellation} onClick={()=>{if(window.confirm('Decline this order? It will remain available to other drivers.'))dispatch(cancelOrder(order.id||order.orderId));}}>Cancel order</Button>}
                  {!readOnly && (
                    <Button
                      variant="success"
                      size="sm"
                      className="driver-order-action"
                      disabled={order.status === 'PENDING' && (!canAccept || !driverCapabilities.accept)}
                      onClick={() => onSelectOrder(order)}
                    >
                      {order.status === 'PENDING' ? 'Accept & Start' : 'Open Flow'}
                    </Button>
                  )}
                </div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>
    </div>
  );
};

export default DriverOrderList;
